import React, { useCallback, useMemo } from "react";

import {
  QButton,
  QCheckbox,
  QFormControl,
  QHeading,
  QInput,
  QStack,
  QTag,
  QText,
} from "@qualio/ui-components";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { TestCasePolicies } from "../../types/createProductStore";
import { INITIAL_TESTCASE_CONFIGURATION } from "../../constants/testCasePolicy";
import { Policy } from "@design-controls/types";

type TestCaseLevel = "testCase1" | "testCase2" | "testCase3";

type FormError = {
  codePrefix?: { message?: string };
  label?: { message?: string };
};

type Props = {
  prefix: TestCaseLevel;
  label: string;
  disableLevel?: (level: TestCaseLevel) => void;
  enableLevel?: (level: TestCaseLevel) => void;
  isDisabled: boolean;
  errors?: FormError;
  onDelete?: (type: Policy["type"]) => void;
};

const TestCaseLevelForm: React.FC<Props> = ({
  prefix,
  label,
  disableLevel,
  enableLevel,
  isDisabled,
  errors,
  onDelete,
}) => {
  const { t } = useTranslation();

  return (
    <QStack borderWidth={1} borderColor="gray.200" borderRadius={2}>
      <QStack padding={4}>
        <QStack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <QHeading size="xs" fontWeight={600}>
            {label}
          </QHeading>
          {isDisabled && (
            <QTag variantColor="gray">
              {t("testCases.policy.tags.disabled")}
            </QTag>
          )}
        </QStack>
        {!isDisabled && (
          <QStack direction="column">
            <QStack direction="row">
              <QFormControl
                label={t("testCases.policy.fields.prefix")}
                width="72px"
                isInvalid={!!errors?.codePrefix}
                error={errors?.codePrefix?.message}
              >
                <Controller
                  name={`${prefix}.codePrefix`}
                  render={({ field: { onChange, value } }) => (
                    <QInput
                      onChange={onChange}
                      value={value}
                      data-cy={`${prefix}-prefix`}
                    />
                  )}
                />
              </QFormControl>
              <QFormControl
                label={t("testCases.policy.fields.name")}
                isInvalid={!!errors?.label}
                error={errors?.label?.message}
              >
                <Controller
                  name={`${prefix}.label`}
                  render={({ field: { onChange, value } }) => (
                    <QInput
                      onChange={onChange}
                      value={value}
                      data-cy={`${prefix}-label`}
                    />
                  )}
                />
              </QFormControl>
            </QStack>
            <QFormControl>
              <Controller
                name={`${prefix}.codeStrategy`}
                defaultValue="GENERATED"
                render={({ field: { onChange, value } }) => (
                  <QCheckbox
                    onChange={(event) => {
                      onChange(
                        event.currentTarget.checked ? "SOURCE" : "GENERATED",
                      );
                    }}
                    value={value}
                    isChecked={value === "SOURCE"}
                    data-cy={`${prefix}-codeStrategy`}
                  >
                    {t("testCases.policy.featureFlags.allowGeneratedCode")}
                  </QCheckbox>
                )}
              />
            </QFormControl>
          </QStack>
        )}
      </QStack>
      {!isDisabled && (
        <QStack backgroundColor="gray.50" padding={2}>
          <QButton
            variant="ghost"
            leftIcon="EyeOff"
            isDisabled={isDisabled}
            data-cy={`disable-${prefix}`}
            onClick={
              disableLevel
                ? () => (onDelete ? onDelete(prefix) : disableLevel(prefix))
                : undefined
            }
          >
            {!!onDelete
              ? t("testCases.policy.actions.delete")
              : t("testCases.policy.actions.disable")}
          </QButton>
        </QStack>
      )}
      {isDisabled && (
        <QStack backgroundColor="gray.50" padding={2}>
          <QButton
            variant="ghost"
            leftIcon="Eye"
            data-cy={`enable-${prefix}`}
            onClick={enableLevel ? () => enableLevel(prefix) : undefined}
          >
            {t("testCases.policy.actions.enable")}
          </QButton>
        </QStack>
      )}
    </QStack>
  );
};

type TestCasePolicyProps = {
  requirements: readonly string[];
  onDelete?: (type: Policy["type"]) => void;
};

export const TestCasePolicy: React.FC<TestCasePolicyProps> = ({
  requirements,
  onDelete,
}) => {
  const { t } = useTranslation();
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<TestCasePolicies>();

  const levels = watch(["testCase1", "testCase2", "testCase3"]);

  const disableLevel = useCallback(
    (level: TestCaseLevel) => {
      setValue(level, undefined);
    },
    [setValue],
  );

  const enableLevel = useCallback(
    (level: TestCaseLevel) => {
      let value = INITIAL_TESTCASE_CONFIGURATION[level];
      if (level === "testCase3") {
        value = {
          codePrefix: "",
          label: "",
          codeStrategy: "GENERATED",
          type: "testCase3",
        };
      }

      setValue(level, value, {
        shouldValidate: true,
      });
    },
    [setValue],
  );

  const disabledLevels = useMemo(() => {
    return {
      testCase1: !levels[0],
      testCase2: !levels[1],
      testCase3: !levels[2],
    };
  }, [levels]);

  return (
    <QStack gap={4} width={640}>
      <QStack>
        <QHeading size="md" fontWeight={600}>
          {t("testCases.policy.heading")}
        </QHeading>
        <QText fontSize="sm">
          {t("testCases.policy.description")}{" "}
          <a href="/registry" target="_blank">
            {t("testCases.policy.resourceLibraryLink")}
          </a>{" "}
          {t("testCases.policy.descriptionSuffix")}
        </QText>
      </QStack>

      {requirements.includes("req1") && (
        <TestCaseLevelForm
          label={t("testCases.policy.levels.level1")}
          isDisabled={disabledLevels["testCase1"]}
          prefix="testCase1"
          disableLevel={disableLevel}
          enableLevel={enableLevel}
          errors={errors.testCase1}
          onDelete={onDelete}
        />
      )}
      {requirements.includes("req2") && (
        <TestCaseLevelForm
          label={t("testCases.policy.levels.level2")}
          isDisabled={disabledLevels["testCase2"]}
          prefix="testCase2"
          disableLevel={disableLevel}
          enableLevel={enableLevel}
          errors={errors.testCase2}
          onDelete={onDelete}
        />
      )}
      {requirements.includes("req3") && (
        <TestCaseLevelForm
          label={t("testCases.policy.levels.level3")}
          isDisabled={disabledLevels["testCase3"]}
          prefix="testCase3"
          disableLevel={disableLevel}
          enableLevel={enableLevel}
          errors={errors.testCase3}
          onDelete={onDelete}
        />
      )}
    </QStack>
  );
};
